/**
 * AudienceBriefMultiPicker — multi-select picker for B2B audience briefs
 * across InsightsAnalysis docs in the current project.
 *
 * Layout (3 panes):
 *   ┌────────────────┬───────────────────────────────────┬─────────────┐
 *   │ Analyses list  │ Audience cards (per analysis)     │ —           │
 *   │ (left rail)    │ each with "Select" checkbox       │             │
 *   └────────────────┴───────────────────────────────────┴─────────────┘
 *  ┌───────────────────────────────────────────────────────────────────┐
 *  │ Selected refs as removable chips                                  │
 *  └───────────────────────────────────────────────────────────────────┘
 *
 * Output (controlled):
 *   value: AudienceBriefRef[]
 *   onChange: (next: AudienceBriefRef[]) => void
 *
 * sourcePath uses the canonical format:
 *   "@insight[<analysisId>].audienceContentBriefs[<audienceId>]"
 *
 * labelSnapshot captures the primary reader role at selection time so the
 * label stays stable if the analysis is later re-run with a new audience id.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Stack,
  Typography,
  Paper,
  Checkbox,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import {
  InsightsFirestoreService,
  type InsightsAnalysisDoc,
} from '@modules/insights-analyzer/services/InsightsFirestoreService';
import {
  formatAudienceLabel,
  getB2BAudienceMetrics,
} from '@modules/insights-analyzer/utils/b2bAudienceMetrics';
import type { AudienceBriefRef } from '@modules/content-plan/types';

const asArray = <T,>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);

const buildSourcePath = (analysisId: string, audienceId: string) =>
  `@insight[${analysisId}].audienceContentBriefs[${audienceId}]`;

const refKey = (r: AudienceBriefRef) => `${r.analysisId}::${r.audienceId}`;

export interface AudienceBriefMultiPickerProps {
  projectId: string;
  value: AudienceBriefRef[];
  onChange: (next: AudienceBriefRef[]) => void;
  /** Optional: max-height for the scrolling list area */
  maxListHeight?: number | string;
}

export const AudienceBriefMultiPicker: React.FC<AudienceBriefMultiPickerProps> = ({
  projectId,
  value,
  onChange,
  maxListHeight = 420,
}) => {
  const [analyses, setAnalyses] = useState<InsightsAnalysisDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedAnalysisId, setSelectedAnalysisId] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!projectId) {
      setAnalyses([]);
      setLoading(false);
      return;
    }
    const svc = new InsightsFirestoreService(projectId);
    setLoading(true);
    setError(null);
    const unsub = svc.onCollectionSnapshot(
      (list) => {
        // Show only analyses that actually have at least one audienceContentBrief —
        // analyses without B2B layer can't contribute selectable items.
        const b2bOnly = list.filter((a) => asArray(a.audienceContentBriefs).length > 0);
        setAnalyses(b2bOnly);
        setLoading(false);
        // Auto-select first analysis on initial load
        setSelectedAnalysisId((current) => current ?? b2bOnly[0]?.id ?? null);
      },
      (err) => {
        setError(err.message ?? 'Failed to load insights analyses');
        setLoading(false);
      },
    );
    return () => unsub();
  }, [projectId]);

  const selectedSet = useMemo(() => new Set(value.map(refKey)), [value]);

  const selectedAnalysis = useMemo(
    () => analyses.find((a) => a.id === selectedAnalysisId) ?? null,
    [analyses, selectedAnalysisId],
  );

  const audienceCards = useMemo(() => {
    if (!selectedAnalysis) return [];
    const briefs = asArray<any>(selectedAnalysis.audienceContentBriefs);
    const mechanics = asArray<any>(selectedAnalysis.audienceBuyingMechanics);
    const readiness = asArray<any>(selectedAnalysis.audienceReadinessProfiles);
    const metrics = getB2BAudienceMetrics(selectedAnalysis);
    const metricByAudience = new Map(metrics.map((m) => [m.audienceId, m]));
    return briefs.map((brief) => {
      const audienceId = brief.audienceId as string;
      const mechanic = mechanics.find((m) => m.audienceId === audienceId);
      const profile = readiness.find((r) => r.audienceId === audienceId);
      const metric = metricByAudience.get(audienceId);
      return {
        audienceId,
        brief,
        mechanic,
        profile,
        metric,
        label: metric ? formatAudienceLabel(metric) : (audienceId || 'audience'),
        primaryReaderRole: metric?.primaryReaderRole as string | undefined,
      };
    });
  }, [selectedAnalysis]);

  const filteredAnalyses = useMemo(() => {
    if (!search.trim()) return analyses;
    const needle = search.toLowerCase();
    return analyses.filter((a) =>
      [a.name, a.summary, a.id].filter(Boolean).some((s) => String(s).toLowerCase().includes(needle)),
    );
  }, [analyses, search]);

  const handleToggle = (analysisId: string, audienceId: string, label: string, primaryReader?: string) => {
    const ref: AudienceBriefRef = {
      analysisId,
      audienceId,
      sourcePath: buildSourcePath(analysisId, audienceId),
      labelSnapshot: primaryReader ? `${label} · ${primaryReader}` : label,
    };
    const key = refKey(ref);
    if (selectedSet.has(key)) {
      onChange(value.filter((r) => refKey(r) !== key));
    } else {
      onChange([...value, ref]);
    }
  };

  const handleRemoveChip = (ref: AudienceBriefRef) => {
    onChange(value.filter((r) => refKey(r) !== refKey(ref)));
  };

  return (
    <Stack spacing={1.5} data-testid="audience-brief-multi-picker">
      {error && <Alert severity="error">{error}</Alert>}

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ minHeight: 360 }}>
        {/* Left rail — analyses list */}
        <Paper variant="outlined" sx={{ flex: '0 0 280px', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ p: 1, borderBottom: 1, borderColor: 'divider' }}>
            <TextField
              size="small"
              fullWidth
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search analyses…"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Box sx={{ flex: 1, overflow: 'auto', maxHeight: maxListHeight }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress size={20} />
              </Box>
            ) : filteredAnalyses.length === 0 ? (
              <Typography color="text.secondary" sx={{ p: 2, fontSize: 13 }}>
                No B2B insights analyses found. Create one in the Insights tab first.
              </Typography>
            ) : (
              <Stack divider={<Box sx={{ borderBottom: 1, borderColor: 'divider' }} />}>
                {filteredAnalyses.map((a) => {
                  const briefsCount = asArray(a.audienceContentBriefs).length;
                  const selectedHere = value.filter((r) => r.analysisId === a.id).length;
                  const active = a.id === selectedAnalysisId;
                  return (
                    <Box
                      key={a.id}
                      onClick={() => setSelectedAnalysisId(a.id)}
                      sx={{
                        p: 1.25,
                        cursor: 'pointer',
                        bgcolor: active ? 'action.selected' : 'transparent',
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                        {a.name || a.id}
                      </Typography>
                      <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                        <Chip size="small" variant="outlined" label={`${briefsCount} audience${briefsCount === 1 ? '' : 's'}`} />
                        {selectedHere > 0 && (
                          <Chip
                            size="small"
                            color="primary"
                            label={`${selectedHere} selected`}
                          />
                        )}
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            )}
          </Box>
        </Paper>

        {/* Right — audience cards */}
        <Paper
          variant="outlined"
          sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
        >
          <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="subtitle2">
              {selectedAnalysis ? (selectedAnalysis.name || selectedAnalysis.id) : 'Pick an analysis on the left'}
            </Typography>
            {selectedAnalysis && (
              <Typography variant="caption" color="text.secondary">
                {audienceCards.length} audience brief{audienceCards.length === 1 ? '' : 's'}
              </Typography>
            )}
          </Box>

          <Box sx={{ flex: 1, overflow: 'auto', maxHeight: maxListHeight }}>
            {!selectedAnalysis ? (
              <Typography color="text.secondary" sx={{ p: 2, fontSize: 13 }}>
                Select an analysis to view its audience briefs.
              </Typography>
            ) : audienceCards.length === 0 ? (
              <Typography color="text.secondary" sx={{ p: 2, fontSize: 13 }}>
                This analysis has no audienceContentBriefs.
              </Typography>
            ) : (
              <Stack spacing={1} sx={{ p: 1.5 }}>
                {audienceCards.map((card) => {
                  const key = `${selectedAnalysis.id}::${card.audienceId}`;
                  const checked = selectedSet.has(key);
                  return (
                    <Paper
                      key={key}
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        borderColor: checked ? 'primary.main' : 'divider',
                        bgcolor: checked ? 'action.selected' : 'background.paper',
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="flex-start">
                        <Checkbox
                          size="small"
                          checked={checked}
                          onChange={() =>
                            handleToggle(
                              selectedAnalysis.id,
                              card.audienceId,
                              card.label,
                              card.primaryReaderRole,
                            )
                          }
                          inputProps={{
                            'aria-label': `Select audience ${card.label}`,
                            'data-testid': `audience-brief-checkbox-${card.audienceId}`,
                          } as any}
                        />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {card.label}
                          </Typography>
                          <Stack direction="row" spacing={0.75} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                            {card.primaryReaderRole && (
                              <Chip size="small" label={`reader: ${card.primaryReaderRole}`} />
                            )}
                            {card.profile?.readinessStage && (
                              <Chip
                                size="small"
                                variant="outlined"
                                color="primary"
                                label={`readiness: ${card.profile.readinessStage}`}
                              />
                            )}
                            {card.profile?.buyingSequenceHint && (
                              <Chip
                                size="small"
                                variant="outlined"
                                color="secondary"
                                label={`sequence: ${card.profile.buyingSequenceHint}`}
                              />
                            )}
                          </Stack>
                          {card.brief?.contentJob && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                              <strong>Content job:</strong> {card.brief.contentJob}
                            </Typography>
                          )}
                          {card.brief?.coreNarrative && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                              <strong>Core narrative:</strong> {card.brief.coreNarrative}
                            </Typography>
                          )}
                          {asArray(card.brief?.mainBarriers).length > 0 && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                              <strong>Top barriers:</strong> {asArray<string>(card.brief.mainBarriers).slice(0, 2).join(' · ')}
                            </Typography>
                          )}
                        </Box>
                      </Stack>
                    </Paper>
                  );
                })}
              </Stack>
            )}
          </Box>
        </Paper>
      </Stack>

      {/* Bottom — selected refs as chips */}
      <Paper variant="outlined" sx={{ p: 1.5 }} data-testid="audience-brief-selected-chips">
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          Selected ({value.length})
        </Typography>
        {value.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Pick at least one audience brief above.
          </Typography>
        ) : (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {value.map((ref) => (
              <Chip
                key={refKey(ref)}
                label={ref.labelSnapshot || `${ref.audienceId}`}
                onDelete={() => handleRemoveChip(ref)}
                deleteIcon={<CloseIcon />}
                size="small"
                color="primary"
              />
            ))}
          </Stack>
        )}
      </Paper>
    </Stack>
  );
};

export default AudienceBriefMultiPicker;
